/**
 * Servicio para registro de usuarios (ej. inscripción taller TNO Academy).
 * Conecta con tnoradio-user-service (POST /api/users/save).
 * Configurar REACT_APP_USER_SERVICE_URL en .env (desarrollo: http://localhost:5001).
 */

const DEFAULT_USER_SERVICE_URL = 'https://api.users.tnonetwork.com';

const getUserServiceBaseUrl = () => {
  const url = process.env.REACT_APP_USER_SERVICE_URL || DEFAULT_USER_SERVICE_URL;
  return (url || '').replace(/\/$/, '');
};

/**
 * Genera slug a partir de nombre y apellido (normalizado, sin acentos).
 */
function slugFromName(name, lastName) {
  const raw = `${(name || '')}${(lastName || '')}`.toLowerCase().trim();
  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
  return normalized || `user-${Date.now()}`;
}

/**
 * Genera una contraseña aleatoria para inscripciones sin campo de contraseña (el usuario puede usar "olvidé mi contraseña" después).
 */
function randomPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < 12; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
}

function splitFullName(fullName = '') {
  const clean = (fullName || '').trim().replace(/\s+/g, ' ');
  if (!clean) return { name: '', lastName: '' };
  const parts = clean.split(' ');
  if (parts.length === 1) return { name: parts[0], lastName: '' };
  return {
    name: parts.slice(0, -1).join(' '),
    lastName: parts.slice(-1).join(' '),
  };
}

/**
 * Registra un usuario para el taller con rol TALLER_REGISTRANT.
 * No se pide contraseña al usuario; se envía una generada internamente (puede recuperar acceso después).
 * El slug del taller (ej. lo que viene después de / en academy.tnonetwork.com/tallerproduccion) se guarda en notas.
 * @param {Object} data - { name, lastName, email, phone, notes, tallerSlug }
 * @returns {Promise<{ success: boolean, user?: object, error?: string }>}
 */
export async function registerTallerUser(data) {
  const baseUrl = getUserServiceBaseUrl();
  if (!baseUrl) {
    return {
      success: false,
      error: 'Servicio de registro no configurado (REACT_APP_USER_SERVICE_URL).',
    };
  }

  const tallerSlug = (data.tallerSlug || '').trim() || 'tallerproduccion';
  const notesBase = 'Inscripción Taller De la Idea al Stream - TNO Academy';
  const notesWithSlug = `${notesBase} | taller: ${tallerSlug}`;
  const userNotes = (data.notes || '').trim();
  const notes = userNotes ? `${notesWithSlug} | ${userNotes}` : notesWithSlug;

  const generatedPassword = randomPassword();
  const slug = slugFromName(data.name, data.lastName);
  const body = {
    name: (data.name || '').trim(),
    lastName: (data.lastName || '').trim(),
    email: { value: (data.email || '').trim().toLowerCase() },
    phone: (data.phone || '').trim(),
    password: generatedPassword,
    passwordConfirmation: generatedPassword,
    roles: [{ role: 'TALLER_REGISTRANT' }],
    slug,
    gender: 'O',
    isActive: true,
    isWorking: false,
    images: [],
    socials: [],
    address: '',
    notes,
    company: '',
    department: '',
    isStarred: false,
    isDeleted: false,
    updatedBy: '',
    turns: [],
    bio: '',
    image: `profile_${slug}`,
    dni: '',
  };

  try {
    const response = await fetch(`${baseUrl}/api/users/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = result.error || result.message || `Error ${response.status}`;
      return { success: false, error: typeof message === 'string' ? message : message.error || 'Error al registrar.' };
    }
    return { success: true, user: result };
  } catch (err) {
    console.error('registerTallerUser error:', err);
    return {
      success: false,
      error: err.message || 'Error de conexión. Intenta de nuevo.',
    };
  }
}

/**
 * Registra un usuario para un curso/mentoría (lead de curso) reutilizando el mismo endpoint.
 * @param {Object} data - { name, lastName, email, phone, notes, courseSlug, courseName }
 * @returns {Promise<{ success: boolean, user?: object, error?: string }>}
 */
export async function registerCourseUser(data) {
  const baseUrl = getUserServiceBaseUrl();
  if (!baseUrl) {
    return {
      success: false,
      error: 'Servicio de registro no configurado (REACT_APP_USER_SERVICE_URL).',
    };
  }

  const courseSlug = (data.courseSlug || '').trim() || 'oratoria';
  const courseName = (data.courseName || '').trim() || 'Oratoria Estratégica';
  const notesBase = `Inscripción Curso ${courseName} - TNO Academy`;
  const notesWithSlug = `${notesBase} | curso: ${courseSlug}`;
  const userNotes = (data.notes || '').trim();
  const notes = userNotes ? `${notesWithSlug} | ${userNotes}` : notesWithSlug;

  const generatedPassword = randomPassword();
  const slug = slugFromName(data.name, data.lastName);
  const body = {
    name: (data.name || '').trim(),
    lastName: (data.lastName || '').trim(),
    email: { value: (data.email || '').trim().toLowerCase() },
    phone: (data.phone || '').trim(),
    password: generatedPassword,
    passwordConfirmation: generatedPassword,
    // Reutilizamos el rol existente para registros de programas/cursos desde landings.
    roles: [{ role: 'TALLER_REGISTRANT' }],
    slug,
    gender: 'O',
    isActive: true,
    isWorking: false,
    images: [],
    socials: [],
    address: '',
    notes,
    company: '',
    department: '',
    isStarred: false,
    isDeleted: false,
    updatedBy: '',
    turns: [],
    bio: '',
    image: `profile_${slug}`,
    dni: '',
  };

  try {
    const response = await fetch(`${baseUrl}/api/users/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = result.error || result.message || `Error ${response.status}`;
      return { success: false, error: typeof message === 'string' ? message : message.error || 'Error al registrar.' };
    }
    return { success: true, user: result };
  } catch (err) {
    console.error('registerCourseUser error:', err);
    return {
      success: false,
      error: err.message || 'Error de conexión. Intenta de nuevo.',
    };
  }
}

/**
 * Registra acreditaciones de prensa para TNOCON Press Start.
 * @param {Object} data
 * @returns {Promise<{ success: boolean, user?: object, error?: string }>}
 */
export async function registerPressStartUser(data) {
  const baseUrl = getUserServiceBaseUrl();
  if (!baseUrl) {
    return {
      success: false,
      error: 'Servicio de registro no configurado (REACT_APP_USER_SERVICE_URL).',
    };
  }

  const { name, lastName } = splitFullName(data.fullName);
  const generatedPassword = randomPassword();
  const slug = slugFromName(name || 'press', lastName || 'start');
  const interviewTarget = (data.interviewTarget || '').trim();
  const notes = [
    'Acreditación de prensa TNOCON "Press Start" 2026',
    `ID/Cédula: ${(data.idNumber || '').trim() || 'N/A'}`,
    `Medio: ${(data.mediaName || '').trim() || 'N/A'}`,
    `Tipo de medio: ${(data.mediaType || '').trim() || 'N/A'}`,
    `Cargo/Función: ${(data.role || '').trim() || 'N/A'}`,
    `Web/Redes: ${(data.mediaLink || '').trim() || 'N/A'}`,
    `Equipo técnico: ${data.hasTechnicalEquipment ? 'Sí' : 'No'}`,
    `Solicita entrevista: ${data.wantsInterview ? 'Sí' : 'No'}`,
    `Talento/Programa: ${interviewTarget || 'N/A'}`,
  ].join(' | ');

  const baseBody = {
    name: (name || '').trim(),
    lastName: (lastName || '').trim(),
    email: { value: (data.email || '').trim().toLowerCase() },
    phone: (data.phone || '').trim(),
    password: generatedPassword,
    passwordConfirmation: generatedPassword,
    slug,
    gender: 'O',
    isActive: true,
    isWorking: false,
    images: [],
    socials: [],
    address: '',
    notes,
    company: (data.mediaName || '').trim(),
    department: '',
    isStarred: false,
    isDeleted: false,
    updatedBy: '',
    turns: [],
    bio: '',
    image: `profile_${slug}`,
    dni: (data.idNumber || '').trim(),
  };

  const configuredRole = (process.env.REACT_APP_PRESSSTART_USER_ROLE || '').trim();
  const roleCandidates = configuredRole
    ? [configuredRole]
    : ['ACREDITACION_PRENSA', 'TALLER_REGISTRANT'];

  try {
    let lastError = null;
    for (const role of roleCandidates) {
      const response = await fetch(`${baseUrl}/api/users/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...baseBody,
          roles: [{ role }],
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        return { success: true, user: result };
      }
      const message = result.error || result.message || `Error ${response.status}`;
      lastError = typeof message === 'string' ? message : message.error || 'Error al registrar.';
    }
    return { success: false, error: lastError || 'Error al registrar.' };
  } catch (err) {
    console.error('registerPressStartUser error:', err);
    return {
      success: false,
      error: err.message || 'Error de conexión. Intenta de nuevo.',
    };
  }
}

/**
 * Registro talento / comunidad creadores — TNOCON Creators Guild (Media Lab).
 * Dispara correo de bienvenida en user-service si las notas contienen el marcador acordado.
 */
export async function registerCreatorsGuildUser(data) {
  const baseUrl = getUserServiceBaseUrl();
  if (!baseUrl) {
    return {
      success: false,
      error: 'Servicio de registro no configurado (REACT_APP_USER_SERVICE_URL).',
    };
  }

  const { name, lastName } = splitFullName(data.fullName);
  const generatedPassword = randomPassword();
  const slug = slugFromName(name || 'creator', lastName || 'guild');
  const programName = (data.programName || '').trim();
  const talentRole = (data.talentRole || '').trim();
  const mediaLink = (data.mediaLink || '').trim();
  const notes = [
    'Registro Creators Guild TNOCON 2026',
    `ID/Cédula: ${(data.idNumber || '').trim() || 'N/A'}`,
    `Programa / show: ${programName || 'N/A'}`,
    `Rol: ${talentRole || 'N/A'}`,
    `Web/Redes: ${mediaLink || 'N/A'}`,
  ].join(' | ');

  const baseBody = {
    name: (name || '').trim(),
    lastName: (lastName || '').trim(),
    email: { value: (data.email || '').trim().toLowerCase() },
    phone: (data.phone || '').trim(),
    password: generatedPassword,
    passwordConfirmation: generatedPassword,
    slug,
    gender: 'O',
    isActive: true,
    isWorking: false,
    images: [],
    socials: [],
    address: '',
    notes,
    company: programName,
    department: '',
    isStarred: false,
    isDeleted: false,
    updatedBy: '',
    turns: [],
    bio: '',
    image: `profile_${slug}`,
    dni: (data.idNumber || '').trim(),
  };

  const configuredRole = (process.env.REACT_APP_CREATORSGUILD_USER_ROLE || '').trim();
  const roleCandidates = configuredRole ? [configuredRole] : ['CREATORS_GUILD', 'HOST'];

  try {
    let lastError = null;
    for (const role of roleCandidates) {
      const response = await fetch(`${baseUrl}/api/users/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...baseBody,
          roles: [{ role }],
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        return { success: true, user: result };
      }
      const message = result.error || result.message || `Error ${response.status}`;
      lastError = typeof message === 'string' ? message : message.error || 'Error al registrar.';
    }
    return { success: false, error: lastError || 'Error al registrar.' };
  } catch (err) {
    console.error('registerCreatorsGuildUser error:', err);
    return {
      success: false,
      error: err.message || 'Error de conexión. Intenta de nuevo.',
    };
  }
}

/**
 * Acreditación corporativa TNOCON día 3 — The Business Level (marcas / mercadeo).
 */
export async function registerBusinessLevelUser(data) {
  const baseUrl = getUserServiceBaseUrl();
  if (!baseUrl) {
    return {
      success: false,
      error: 'Servicio de registro no configurado (REACT_APP_USER_SERVICE_URL).',
    };
  }

  const { name, lastName } = splitFullName(data.fullName);
  const generatedPassword = randomPassword();
  const slug = slugFromName(name || 'business', lastName || 'level');
  const brand = (data.companyBrand || '').trim();
  const interests = [
    data.interestSponsorship && 'Patrocinio programas en vivo',
    data.interestMediaLab && 'Producción contenido original (Media Lab)',
    data.interestWeb && 'Presencia web, TNO Digest y editorial',
  ].filter(Boolean);
  const interestsLine = interests.length ? interests.join('; ') : 'No especificado';
  const notes = [
    'Registro The Business Level TNOCON 2026',
    `Cargo: ${(data.representativeRole || '').trim() || 'N/A'}`,
    `Marca: ${brand || 'N/A'}`,
    `Sector: ${(data.sector || '').trim() || 'N/A'}`,
    `Web/Redes: ${(data.webSocials || '').trim() || 'N/A'}`,
    `Intereses: ${interestsLine}`,
  ].join(' | ');

  const baseBody = {
    name: (name || '').trim(),
    lastName: (lastName || '').trim(),
    email: { value: (data.email || '').trim().toLowerCase() },
    phone: (data.corporateWhatsapp || data.phone || '').trim(),
    password: generatedPassword,
    passwordConfirmation: generatedPassword,
    slug,
    gender: 'O',
    isActive: true,
    isWorking: false,
    images: [],
    socials: [],
    address: '',
    notes,
    company: brand,
    department: (data.sector || '').trim(),
    isStarred: false,
    isDeleted: false,
    updatedBy: '',
    turns: [],
    bio: interestsLine,
    image: `profile_${slug}`,
    dni: '',
  };

  const configuredRole = (process.env.REACT_APP_BUSINESS_LEVEL_USER_ROLE || '').trim();
  const roleCandidates = configuredRole ? [configuredRole] : ['BUSINESS_LEVEL', 'POTENTIAL_CLIENT'];

  try {
    let lastError = null;
    for (const role of roleCandidates) {
      const response = await fetch(`${baseUrl}/api/users/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...baseBody,
          roles: [{ role }],
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        return { success: true, user: result };
      }
      const message = result.error || result.message || `Error ${response.status}`;
      lastError = typeof message === 'string' ? message : message.error || 'Error al registrar.';
    }
    return { success: false, error: lastError || 'Error al registrar.' };
  } catch (err) {
    console.error('registerBusinessLevelUser error:', err);
    return {
      success: false,
      error: err.message || 'Error de conexión. Intenta de nuevo.',
    };
  }
}

const userService = {
  registerTallerUser,
  registerCourseUser,
  registerPressStartUser,
  registerCreatorsGuildUser,
  registerBusinessLevelUser,
  getUserServiceBaseUrl,
};

export default userService;
